/**
 * Validates an optional OAuth redirect URI **origin** (scheme + host + optional port)
 * for Jack Henry / Banno. Used by admin UI and API server.
 *
 * Empty / whitespace-only input means "derive from the incoming API request host".
 */
export type OptionalBannoOAuthRedirectBaseResult =
  | { ok: true; origin: string | null }
  | { ok: false; message: string }

const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]'])

function hostnameIsLocalhost(hostname: string): boolean {
  return LOCAL_HOSTNAMES.has(hostname.toLowerCase())
}

export function validateOptionalBannoOAuthRedirectUrlBase(
  raw: unknown
): OptionalBannoOAuthRedirectBaseResult {
  if (raw === undefined || raw === null) {
    return { ok: true, origin: null }
  }
  if (typeof raw !== 'string') {
    return { ok: false, message: 'Redirect URL base must be text or empty.' }
  }
  const trimmed = raw.trim()
  if (!trimmed) {
    return { ok: true, origin: null }
  }

  let parsed: URL
  try {
    parsed = new URL(trimmed)
  } catch {
    return {
      ok: false,
      message: 'Enter a valid absolute URL (e.g. https://api.example.com).',
    }
  }

  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    return { ok: false, message: 'Only http and https are allowed.' }
  }

  if (parsed.username || parsed.password) {
    return { ok: false, message: 'URL must not include a username or password.' }
  }

  if (parsed.search || parsed.hash) {
    return { ok: false, message: 'URL must not include a query or fragment.' }
  }

  const path = parsed.pathname || ''
  if (path !== '' && path !== '/') {
    return {
      ok: false,
      message: 'Use only the site origin (no path), e.g. https://api.example.com',
    }
  }

  if (!parsed.hostname) {
    return { ok: false, message: 'URL must include a hostname.' }
  }

  if (parsed.protocol === 'http:' && !hostnameIsLocalhost(parsed.hostname)) {
    return {
      ok: false,
      message: 'http is only allowed for localhost / 127.0.0.1. Use https for other hosts.',
    }
  }

  return { ok: true, origin: parsed.origin }
}
